import { PickInfo } from '@deck.gl/core/lib/deck';
import { IconLayer, LineLayer, ScatterplotLayer, TextLayer } from 'deck.gl';
import imageMarker from '../../../../assets/icon/locate2.png';
import { IMarker } from '../../../../types/marker';
import { ILabelLayer } from '../../../../types/map';

export const FWELAYER = 'fwe-point-layer';
export const INSITU_LAYER = 'insitu-point-layer';
export const INSITU_UPLOAD_LAYER = 'insitu-upload-layer';
export const INSITU_EXPANDED_LAYER = 'insitu-expanded-layer';

export function FWEPointLayer<IFWPoint>(
  data: IFWPoint[]
): ScatterplotLayer<IFWPoint> {
  const layer = new ScatterplotLayer({
    id: FWELAYER,
    data,
    pickable: true,
    getPosition: (d: any) => d.position,
    getFillColor: [188, 188, 35],
    getLineColor: [0, 0, 0],
    getRadius: 8,
    radiusScale: 1,
    radiusMinPixels: 8,
    radiusMaxPixels: 8,
    getLineWidth: 2,
    stroked: true,
    lineWidthUnits: 'pixels',
  });
  return layer as ScatterplotLayer<IFWPoint>;
}

export function InSituPointLayer<InSituPoint>(
  data: InSituPoint[]
): ScatterplotLayer<InSituPoint> {
  const layer = new ScatterplotLayer({
    id: INSITU_LAYER,
    data,
    pickable: true,
    getPosition: (d: any) => d.position,
    getFillColor: [50, 205, 50],
    getLineColor: [0, 0, 0],
    getRadius: 8,
    radiusScale: 1,
    radiusMinPixels: 8,
    radiusMaxPixels: 8,
    getLineWidth: 2,
    stroked: true,
    lineWidthUnits: 'pixels',
  });

  return layer as ScatterplotLayer<InSituPoint>;
}

export function ExpandInSituLayer(data: any) {
  const { expandedPoints, lines } = data;

  const layers = [
    new LineLayer({
      id: `${INSITU_EXPANDED_LAYER}-line`,
      data: lines,
      pickable: true,
      getSourcePosition: (d: any) => d.sourcePosition,
      getTargetPosition: (d: any) => d.targetPosition,
      getColor: [128, 128, 128],
      getWidth: 1,
      widthUnits: 'pixels',
    }),
    new ScatterplotLayer({
      id: INSITU_EXPANDED_LAYER,
      data: expandedPoints,
      pickable: true,
      getPosition: (d: any) => d.position,
      getFillColor: [50, 255, 50],
      getLineColor: [0, 0, 0],
      getRadius: 6,
      radiusScale: 1,
      radiusMinPixels: 6,
      radiusMaxPixels: 6,
      getLineWidth: 1,
      stroked: true,
      lineWidthUnits: 'pixels',
    }),
  ];

  return layers;
}
export function MarkerLayer(
  data: IMarker[],
  onDrag?: (info: PickInfo<IMarker>, e: MouseEvent) => void,
  onDragStart?: (info: PickInfo<IMarker>, e: MouseEvent) => void,
  onDragEnd?: (info: PickInfo<IMarker>, e: MouseEvent) => void
) {
  const ICON_MAPPING = {
    marker: { x: 0, y: 0, width: 128, height: 128, anchorY: 125, mask: true },
  };

  const layer = new IconLayer<IMarker>({
    id: 'icon-layer',
    data,
    pickable: true,
    iconAtlas: imageMarker,
    iconMapping: ICON_MAPPING,
    getIcon: (d) => 'marker',
    getPosition: (d) => d.coordinates as [number, number],
    getSize: (d) => 40,
    getColor: (d) => (d.active ? [0, 164, 236, 255] : [134, 162, 179, 255]),
    onDragStart: (info: PickInfo<IMarker>, e: any) => {
      return onDragStart && onDragStart(info, e);
    },
    onDrag: (info: PickInfo<IMarker>, e: any) => {
      return onDrag && onDrag(info, e);
    },
    onDragEnd: (info: PickInfo<IMarker>, e: any) => {
      return onDragEnd && onDragEnd(info, e);
    },
  });

  const transparentLayer = new ScatterplotLayer<IMarker>({
    id: 'icon-transparent',
    data,
    pickable: true,
    getPosition: (d) => d.coordinates as [number, number],
    getRadius: () => 25,
    getFillColor: [0, 0, 0, 0],
    getLineColor: [0, 0, 0, 0],
    lineWidthMinPixels: 2,
    radiusMinPixels: 25,
    radiusMaxPixels: 25,
    onDragStart: (info: PickInfo<IMarker>, e: any) => {
      return onDragStart && onDragStart(info, e);
    },
    onDrag: (info: PickInfo<IMarker>, e: any) => {
      return onDrag && onDrag(info, e);
    },
    onDragEnd: (info: PickInfo<IMarker>, e: any) => {
      return onDragEnd && onDragEnd(info, e);
    },
  });

  return [layer, transparentLayer];
}

export function LabelLayer(data: ILabelLayer[]) {
  const layer = new TextLayer({
    id: 'label-layer',
    data,
    pickable: false,
    getPosition: (d: any) => d.coordinates,
    getText: (d: any) => d.name,
    getColor: (d: any) => {
      if (d.active) {
        return [0, 164, 236];
      }
      return [134, 162, 179, 255];
    },
    getSize: (d: any) => (d.textSize ? d.textSize : 18), //24,
    getAngle: 0,
    getTextAnchor: (d: any) => (d.textAnchor ? d.textAnchor : 'start'), //'start' | 'middle' | 'end',
    getAlignmentBaseline: (d: any) =>
      d.alignmentBaseline ? d.alignmentBaseline : 'top', //'top' | 'center' | 'bottom',
    getPixelOffset: (d: any) => (d.pixelOffset ? d.pixelOffset : [0, 0]),
  });
  return layer;
}
